import {
  ActionBar,
  ChakraFlex,
  ChakraGrid,
  ChakraGridItem,
  coalescePlainTextToEditorV2Html,
  DatePickerInput,
  EditorV2,
  EditorV2State,
  FAIconName,
  FireflyButton,
  InputGroupV2,
  ModalV2,
  Text,
  UniversalSelect,
} from '@equip.health/ui';
import { useMemo, useRef } from 'react';
import SectionHeader from '~/components/common/SectionHeader';
import { getSelectedUniversalSelectOption } from '~/lib/util/inquiry.util';
import {
  CodeAndLabel,
  CodeAndLabelWithSortOrder,
  SecondaryDischargeReason,
  useGetIsPatientSeekingCaresQuery,
  useGetLevelsOfCareQuery,
  useGetSecondaryReasonsQuery,
  useGetTypesOfCareQuery,
  useGetWhoInitiatedDischargesQuery,
} from '~/store/services/clinical-data/refDischargePlan';
import { useDiscardChangesModalContext } from '../common/DiscardChangesModalContext';
import { getAutoSelectedSecondaryReason } from '../util/dischargePlanUtils';
import { useFormikContext } from 'formik';
import { EditDischargeDetailsFormData } from '~/lib/types/episode/dischargePlan/EditDischargeDetailsFormData';
import { useGetTreatmentEpisodeStatusReasonsEnumQuery } from '~/store/services/enumerations';
import { PatientUniversalStatusCode } from '~/lib/constants/patients.constants';
import {
  CharacterLimit,
  IsPatientSeekingCares,
} from '../common/dischargePlanConstants';
import { getMultiSelectedUniversalSelectOptions } from '~/lib/util/profile.util';

interface EditDischargeDetailsModalProps {
  isOpen: boolean;
  isLoading: boolean;
}

const EditDischargeDetailsModal = ({
  isOpen,
  isLoading,
}: EditDischargeDetailsModalProps) => {
  const bannerInfoText = (
    <Text>
      <Text
        id="tentative-discharge-date-banner-info"
        display="inline"
        variant="bodyMedium"
      >
        Tentative discharge date
      </Text>{' '}
      will be displayed and referenced across Maud, including assigning
      discharge planning tasks to the current user, so please keep it updated.
    </Text>
  );
  const additionalDetailsEditorRef = useRef<EditorV2State | null>(null);
  const statusAtDischargeEditorRef = useRef<EditorV2State | null>(null);

  const opts = { skip: !isOpen };
  // rtk queries
  const { data: isPatientSeekingCaresData } = useGetIsPatientSeekingCaresQuery(
    undefined,
    opts,
  );
  const { data: levelsOfCareData } = useGetLevelsOfCareQuery(undefined, opts);
  const { data: primaryReasonsData } =
    useGetTreatmentEpisodeStatusReasonsEnumQuery(undefined, opts);
  const { data: secondaryReasonsData } = useGetSecondaryReasonsQuery(
    undefined,
    opts,
  );
  const { data: typesOfCareData } = useGetTypesOfCareQuery(undefined, opts);
  const { data: whoInitiatedDischargesData } =
    useGetWhoInitiatedDischargesQuery(undefined, opts);

  // hooks
  const { onCloseModal } = useDiscardChangesModalContext();
  const {
    values: formValues,
    setFieldValue,
    dirty: hasAnyFormDataChanges,
    submitForm,
  } = useFormikContext<EditDischargeDetailsFormData>();

  // globals / constants
  const htmlInitialAdditionalDetails: string | null =
    formValues.additionalDischargeReasonDetails
      ? coalescePlainTextToEditorV2Html(
          formValues.additionalDischargeReasonDetails,
        )
      : null;
  const htmlInitialStatusAtDischarge: string | null =
    formValues.statusAtDischarge
      ? coalescePlainTextToEditorV2Html(formValues.statusAtDischarge)
      : null;

  // mappers
  const mapTopUniversalSelectOptions = (
    options:
      | (CodeAndLabel | CodeAndLabelWithSortOrder | SecondaryDischargeReason)[]
      | undefined,
  ): UniversalSelectOption[] => {
    return (options ?? []).map(({ label, code }: CodeAndLabel) => ({
      label,
      value: code,
    }));
  };

  const mapPrimaryReasonUniversalSelectOptions = (
    options: EpisodeStatusReason[] | undefined,
  ): UniversalSelectOption[] => {
    return (options ?? [])
      .filter((pr) => pr.status === PatientUniversalStatusCode.DISCHARGED)
      .map(({ label, externalId }) => ({
        label,
        value: externalId,
      }));
  };

  // dropdown memos
  const whoInitiatedDischargeOptions = useMemo<UniversalSelectOption[]>(() => {
    return mapTopUniversalSelectOptions(whoInitiatedDischargesData);
  }, [whoInitiatedDischargesData]);
  const isPatientSeekingCaresOptions = useMemo<UniversalSelectOption[]>(() => {
    return mapTopUniversalSelectOptions(isPatientSeekingCaresData);
  }, [isPatientSeekingCaresData]);
  const primaryDischargeReasonOptions = useMemo<UniversalSelectOption[]>(() => {
    return mapPrimaryReasonUniversalSelectOptions(primaryReasonsData);
  }, [primaryReasonsData]);
  const secondaryDischargeReasonOptions = useMemo<
    UniversalSelectOption[]
  >(() => {
    const filteredByPrimaryReasons = (secondaryReasonsData ?? []).filter(
      (sr) =>
        sr.primaryDischargeReason.statusReasonExternalId ===
        formValues.primaryDischargeReason?.statusReasonExternalId,
    );
    return mapTopUniversalSelectOptions(filteredByPrimaryReasons);
  }, [formValues.primaryDischargeReason, secondaryReasonsData]);
  const typesOfCareOptions = useMemo<UniversalSelectOption[]>(() => {
    return mapTopUniversalSelectOptions(typesOfCareData);
  }, [typesOfCareData]);
  const levelsOfCareOptions = useMemo<UniversalSelectOption[]>(() => {
    return mapTopUniversalSelectOptions(levelsOfCareData);
  }, [levelsOfCareData]);

  const isPatientSeekingCare =
    formValues.isPatientSeekingCare?.code === IsPatientSeekingCares.YES.code;

  return (
    <ModalV2
      footerContent={
        <ChakraFlex
          alignItems="center"
          justifyContent="space-between"
          marginTop="24px"
          width="100%"
        >
          <FireflyButton
            id="close-edit-discharge-details"
            isLoading={isLoading}
            leftIcon={FAIconName.timesRegular}
            onClick={() => onCloseModal(hasAnyFormDataChanges)}
            variant="secondary"
          >
            Close
          </FireflyButton>
          <FireflyButton
            id="save-edit-discharge-details"
            isDisabled={!hasAnyFormDataChanges}
            isLoading={isLoading}
            leftIcon={FAIconName.checkRegular}
            onClick={submitForm}
          >
            Save changes
          </FireflyButton>
        </ChakraFlex>
      }
      isLoading={isLoading}
      isOpen={isOpen}
      onClose={() => onCloseModal(hasAnyFormDataChanges)}
      size="3xl"
      title="Edit discharge details"
    >
      <ChakraFlex
        flexDirection="column"
        gap="24px"
        overflowY="auto"
        paddingRight="10px"
      >
        <Text>
          All fields, except those marked optional, are required before marking
          the patient “ready to discharge”
        </Text>
        <ChakraGrid gap="8px" templateColumns={{ base: 'repeat(2, 1fr)' }}>
          <ChakraGridItem>
            <InputGroupV2
              id="tentative-discharge-date"
              label="Tentative discharge date"
              tooltipIconOptions={{
                iconColor: 'neutral.secondary',
                iconName: FAIconName.infoCircleRegular,
                id: 'tentative-discharge-date',
                placement: 'bottom-start',
                tooltipText:
                  "The tentative discharge date is an estimate to help the care team prepare for an upcoming discharge. At discharge, the actual discharge date will be set to the date of the patient's last session with Equip.",
                width: '300px',
              }}
            >
              <DatePickerInput
                defaultDate={formValues.tentativeDischargeDate}
                id="tentative-discharge"
                onSubmit={(date: Date) =>
                  setFieldValue('tentativeDischargeDate', date)
                }
                placeholder="Please choose"
                showTimeInput={false}
              />
            </InputGroupV2>
          </ChakraGridItem>
          <ChakraGridItem>
            <InputGroupV2
              id="who-initiated-discharge"
              label="Who initiated the discharge conversation?"
            >
              <UniversalSelect
                aria-labelledby="who-initiated-discharge"
                ariaLabel="Who initiated the discharge conversation?"
                id="who-initiated-discharge"
                isClearable
                isSingleSelect
                onChange={(e) => {
                  if (e === null) {
                    setFieldValue('whoInitiatedDischarge', null);
                    return;
                  }

                  const selectedOption = whoInitiatedDischargesData?.find(
                    (d) => d.code === e.value,
                  );
                  setFieldValue('whoInitiatedDischarge', selectedOption);
                }}
                options={whoInitiatedDischargeOptions}
                placeholder="Please choose"
                value={getSelectedUniversalSelectOption(
                  formValues.whoInitiatedDischarge?.code ?? null,
                  whoInitiatedDischargeOptions,
                )}
              />
            </InputGroupV2>
          </ChakraGridItem>
        </ChakraGrid>

        <ChakraFlex flexDirection="column" gap="16px">
          <ActionBar
            id="tentative-discharge-date-info"
            text={bannerInfoText}
            variant="info"
          />
          <SectionHeader
            headerText="Reason for discharge"
            variant="bodyMedium"
          />
          <ChakraGrid gap="20px" templateColumns={{ base: 'repeat(2, 1fr)' }}>
            <ChakraGridItem>
              <InputGroupV2
                id="primary-discharge-reason"
                label="Primary discharge reason"
              >
                <UniversalSelect
                  aria-labelledby="primary-discharge-reason"
                  ariaLabel="Primary discharge reason"
                  id="primary-discharge-reason"
                  isClearable
                  isSingleSelect
                  onChange={(e) => {
                    if (e === null) {
                      setFieldValue('primaryDischargeReason', null);
                      setFieldValue('secondaryDischargeReason', null);
                      return;
                    }
                    const prData = primaryReasonsData ?? [];
                    const selectedPrimaryReason = prData.find(
                      (prd) => prd.externalId === e.value,
                    );
                    const hasChangedPrimaryReason =
                      selectedPrimaryReason?.externalId !==
                      formValues.primaryDischargeReason?.statusReasonExternalId;
                    if (!hasChangedPrimaryReason) {
                      return;
                    }
                    setFieldValue('primaryDischargeReason', {
                      statusReasonExternalId:
                        selectedPrimaryReason?.externalId ?? '',
                      statusReasonName: selectedPrimaryReason?.label,
                    });

                    // AUTO-POPULATE: If exactly 1 secondary reason, auto-select it
                    const autoSelectedSecondary =
                      getAutoSelectedSecondaryReason(
                        selectedPrimaryReason?.externalId,
                        secondaryReasonsData,
                      );
                    setFieldValue(
                      'secondaryDischargeReason',
                      autoSelectedSecondary,
                    );
                  }}
                  options={primaryDischargeReasonOptions}
                  placeholder="Please choose"
                  value={getSelectedUniversalSelectOption(
                    formValues.primaryDischargeReason?.statusReasonExternalId ??
                      null,
                    primaryDischargeReasonOptions,
                  )}
                />
              </InputGroupV2>
            </ChakraGridItem>
            {secondaryDischargeReasonOptions?.length > 0 && (
              <ChakraGridItem>
                <InputGroupV2
                  id="secondary-discharge-reason"
                  isRequired={secondaryDischargeReasonOptions.length > 0}
                  label="Secondary discharge reason"
                >
                  <UniversalSelect
                    aria-labelledby="secondary-discharge-reason"
                    ariaLabel="Secondary discharge reason"
                    id="secondary-discharge-reason"
                    isClearable
                    isSingleSelect
                    onChange={(e) => {
                      if (e === null) {
                        setFieldValue('secondaryDischargeReason', null);
                        return;
                      }
                      const selectedSecondaryReason =
                        secondaryReasonsData?.find(
                          (srd) => srd.code === e.value,
                        );
                      setFieldValue(
                        'secondaryDischargeReason',
                        selectedSecondaryReason,
                      );
                    }}
                    options={secondaryDischargeReasonOptions}
                    placeholder="Please choose"
                    value={getSelectedUniversalSelectOption(
                      formValues.secondaryDischargeReason?.code ?? null,
                      secondaryDischargeReasonOptions,
                    )}
                  />
                </InputGroupV2>
              </ChakraGridItem>
            )}
            <ChakraGridItem colSpan={2}>
              <InputGroupV2
                id="additional-discharge-reason-details"
                isRequired={false}
                label="Additional details about reason for discharge"
              >
                {/** TODO add onFocus and onBlur Mixpanel metrics */}
                <EditorV2
                  characterLimit={CharacterLimit}
                  containerTestId="additional-discharge-reason-details"
                  initialContent={htmlInitialAdditionalDetails}
                  onChange={(val) => {
                    setFieldValue(
                      'additionalDischargeReasonDetails',
                      !additionalDetailsEditorRef.current?.isEmpty ? val : null,
                    );
                  }}
                  placeholderText="If applicable, provide any context around the reason for the patient's discharge"
                  stateRef={additionalDetailsEditorRef}
                />
              </InputGroupV2>
            </ChakraGridItem>
          </ChakraGrid>
        </ChakraFlex>

        <ChakraFlex flexDirection="column" gap="16px">
          <SectionHeader
            headerText="Patient care post-Equip"
            variant="bodyMedium"
          />
          <ChakraGrid gap="20px" templateColumns={{ base: 'repeat(2, 1fr)' }}>
            <ChakraGridItem colSpan={2}>
              <InputGroupV2
                id="is-patient-seeking-care"
                label="Is the patient seeking care after Equip? This includes Equip-provided referrals & community options."
              >
                <UniversalSelect
                  aria-labelledby="is-patient-seeking-care"
                  ariaLabel="Is the patient seeking care after Equip? This includes Equip-provided referrals & community options."
                  id="is-patient-seeking-care"
                  isClearable
                  isSingleSelect
                  onChange={(e) => {
                    if (e === null) {
                      setFieldValue('isPatientSeekingCare', null);
                      setFieldValue('typeOfCare', []);
                      setFieldValue('levelOfCare', []);
                      return;
                    }
                    const selectedOption = isPatientSeekingCaresData?.find(
                      (d) => d.code === e.value,
                    );
                    setFieldValue('isPatientSeekingCare', selectedOption);

                    const isNoLongerSeekingCare =
                      isPatientSeekingCare &&
                      e.value !== IsPatientSeekingCares.YES.code;
                    if (isNoLongerSeekingCare) {
                      setFieldValue('typeOfCare', []);
                      setFieldValue('levelOfCare', []);
                    }
                  }}
                  options={isPatientSeekingCaresOptions}
                  placeholder="Please choose"
                  value={getSelectedUniversalSelectOption(
                    formValues.isPatientSeekingCare?.code,
                    isPatientSeekingCaresOptions,
                  )}
                />
              </InputGroupV2>
            </ChakraGridItem>
            {isPatientSeekingCare && (
              <>
                <ChakraGridItem colSpan={2}>
                  <InputGroupV2
                    id="types-of-care"
                    label="What type(s) of care is the patient seeking?"
                  >
                    <UniversalSelect
                      aria-labelledby="types-of-care"
                      ariaLabel="What type(s) of care is the patient seeking?"
                      id="types-of-care"
                      isClearable
                      isSingleSelect={false}
                      onChange={(selectedOptions) => {
                        if (selectedOptions === null) {
                          setFieldValue('typeOfCare', []);
                          return;
                        }
                        const tocData = typesOfCareData ?? [];
                        const selectedTypesOfCare = tocData.filter((toc) =>
                          selectedOptions.some((so) => so.value === toc.code),
                        );
                        setFieldValue('typeOfCare', selectedTypesOfCare);
                      }}
                      options={typesOfCareOptions}
                      placeholder="Please choose"
                      value={getMultiSelectedUniversalSelectOptions(
                        formValues.typeOfCare.map((toc) => toc.code),
                        typesOfCareOptions,
                      )}
                    />
                  </InputGroupV2>
                </ChakraGridItem>
                <ChakraGridItem colSpan={2}>
                  <InputGroupV2
                    id="levels-of-care"
                    label="What level(s) of care is the patient seeking?"
                  >
                    <UniversalSelect
                      aria-labelledby="levels-of-care"
                      ariaLabel="What level(s) of care is the patient seeking?"
                      id="levels-of-care"
                      isClearable
                      isSingleSelect={false}
                      onChange={(selectedOptions) => {
                        if (selectedOptions === null) {
                          setFieldValue('levelOfCare', []);
                          return;
                        }
                        const locData = levelsOfCareData ?? [];
                        const selectedLevelsOfCare = locData.filter((loc) =>
                          selectedOptions.some((so) => so.value === loc.code),
                        );
                        setFieldValue('levelOfCare', selectedLevelsOfCare);
                      }}
                      options={levelsOfCareOptions}
                      placeholder="Please choose"
                      value={getMultiSelectedUniversalSelectOptions(
                        formValues.levelOfCare.map((loc) => loc.code),
                        levelsOfCareOptions,
                      )}
                    />
                  </InputGroupV2>
                </ChakraGridItem>
              </>
            )}
          </ChakraGrid>
        </ChakraFlex>

        <ChakraFlex flexDirection="column" gap="16px">
          <SectionHeader headerText="Patient status" variant="bodyMedium" />
          <ChakraGrid gap="8px" templateColumns={{ base: 'repeat(2, 1fr)' }}>
            <ChakraGridItem colSpan={2}>
              <InputGroupV2
                id="patient-status-at-discharge"
                isRequired={false}
                label="Patient status at discharge"
              >
                {/** TODO add onFocus and onBlur Mixpanel metrics */}
                <EditorV2
                  characterLimit={CharacterLimit}
                  containerTestId="patient-status-at-discharge"
                  initialContent={htmlInitialStatusAtDischarge}
                  onChange={(val) => {
                    setFieldValue(
                      'statusAtDischarge',
                      !statusAtDischargeEditorRef.current?.isEmpty ? val : null,
                    );
                  }}
                  placeholderText="Describe symptoms including nutritional status, etc"
                  stateRef={statusAtDischargeEditorRef}
                />
              </InputGroupV2>
            </ChakraGridItem>
          </ChakraGrid>
        </ChakraFlex>
      </ChakraFlex>
    </ModalV2>
  );
};

export default EditDischargeDetailsModal;
